<script lang="ts">
  import { push } from "svelte-spa-router";
  import { api } from "../../framework/js/api.js";

  let projectName = "";
  let started = false;
  let submitting = false;

  function defaultName(): string {
    const date = new Date().toLocaleDateString(navigator.language, { dateStyle: "medium" });
    return `Discovery · ${date}`;
  }

  function startConversation() {
    started = true;
  }

  async function createBrief() {
    if (submitting) return;
    submitting = true;
    try {
      const name = projectName.trim() || defaultName();
      const body: any = await api.addXploritAndBrief(name);
      if (body.success) {
        push(`/brief/${body.data.xploritBrief.uuid}/continue`);
      } else {
        alert("Failed to start the conversation");
        submitting = false;
      }
    } catch (error) {
      console.error("Failed to create brief", error);
      submitting = false;
    }
  }
</script>

<div class="w640 wizard-wrap">
  <div class="wizard-header">
    <span class="wizard-eyebrow">
      <i class="pencil alternate icon" aria-hidden="true"></i>
      Built-to-Brief
    </span>
  </div>

  {#if !started}
    <div class="start">
      <p class="kicker">This is not a form. It is a conversation.</p>
      <h1>A few questions. Then I write the brief.</h1>
      <p class="lede">
        I will ask what a senior researcher would ask. Then I will sit with it and write a thinking document, not a restatement of what you typed.
      </p>
      <button type="button" class="start-btn" on:click={startConversation}>
        Start the conversation
      </button>
    </div>
  {:else}
    <form on:submit|preventDefault={createBrief} class="ui form">
      <div class="step-body">
        <label for="projectName" class="brief-question">Give this a name, if you like</label>
        <p class="step-prompt">Or leave it. I will name it for today.</p>
        <input
          type="text"
          id="projectName"
          bind:value={projectName}
          placeholder={defaultName()}
          maxlength="100"
          class="brief-textarea brief-textarea-step-1 input_bg"
        />
      </div>
      <div class="nav-row">
        <button type="button" class="ghost" on:click={() => (started = false)}>Back</button>
        <button type="submit" class="start-btn" disabled={submitting}>
          {submitting ? "Starting…" : "Begin"}
        </button>
      </div>
    </form>
  {/if}
</div>

<style>
  .wizard-wrap {
    margin: 0 auto;
    padding: 3rem 1.5rem;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .wizard-header {
    display: flex;
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  .wizard-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--primary);
    background: rgba(5, 108, 242, 0.1);
    border-radius: 999px;
    letter-spacing: 0.02em;
  }

  .start h1,
  .brief-question {
    margin: 0 0 0.75rem;
    font-size: 1.75rem;
    font-weight: 500;
    color: var(--grey);
    text-align: left;
  }

  .kicker {
    margin: 0 0 0.5rem;
    font-weight: 500;
    color: var(--primary);
  }

  .lede,
  .step-prompt {
    margin: 0 0 1.5rem;
    color: var(--grey-secondary);
    font-size: 1.05rem;
    line-height: 1.5;
    text-align: left;
  }

  .start-btn,
  .ghost {
    border: 0;
    cursor: pointer;
    border-radius: 6px;
    padding: 0.85rem 1.25rem;
    font-weight: 500;
  }

  .start-btn {
    background: var(--primary);
    color: var(--color-bright);
  }

  .start-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .ghost {
    background: transparent;
    color: var(--grey);
  }

  .start-btn:focus-visible,
  .ghost:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }

  .nav-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 2rem;
  }

  input[type="text"] {
    width: 100%;
    box-sizing: border-box;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-button);
    background-color: rgb(var(--input-bg));
  }

  input[type="text"]:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }

  @media (max-width: 768px) {
    .wizard-wrap {
      padding: 1.5rem 1rem;
    }
    .start h1 {
      font-size: 1.4rem;
    }
  }
</style>
